import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useCms } from '../cms/CmsContext';
import toast from '../utils/toast';
import AdminUnsoldAssignModal from '../components/AdminUnsoldAssignModal';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { trackAddToCart, trackViewContent } from '../utils/metaPixel';
import { isCurrentUserStrictAdmin } from '../utils/adminCheck';
import './Tickets.css';
import './MusicEntertainment.css';
import { getCmsImagePath } from '../cms/cmsData';
import { getArabicTranslation } from '../utils/arabicTranslations';
import { buildLineupDaySections, getArtistLineup } from '../data/artistLineup';

const MAX_FESTIVAL_TICKETS_PER_ORDER = 50;

const detectFestivalPassKey = (rawSlug = '', rawName = '', rawValidDays = []) => {
  const slug = String(rawSlug || '').toLowerCase();
  const name = String(rawName || '').toLowerCase();
  const haystack = `${slug} ${name}`;
  const blockedTerms = ['vendor', 'booth', 'bazaar', 'food', 'truck', 'amp', 'amphithe', 'fifa', 'hookah', 'ride', 'wristband'];
  if (blockedTerms.some((term) => haystack.includes(term))) return '';

  if (
    haystack.includes('3day')
    || haystack.includes('3-day')
    || haystack.includes('3 day')
    || haystack.includes('3days')
    || haystack.includes('3-days')
    || haystack.includes('three-day')
    || haystack.includes('three day')
    || haystack.includes('three days')
  ) return '3day';
  if (haystack.includes('2day') || haystack.includes('2-day') || haystack.includes('2 day') || haystack.includes('2days') || haystack.includes('2-days') || haystack.includes('two-day') || haystack.includes('two day') || haystack.includes('two days')) return '2day';
  if (haystack.includes('1day') || haystack.includes('1-day') || haystack.includes('1 day') || haystack.includes('1days') || haystack.includes('1-days') || haystack.includes('one-day') || haystack.includes('one day') || haystack.includes('one days')) return '1day';

  const looksLikeFestivalPass = haystack.includes('festival') || haystack.includes('pass') || haystack.includes('day');
  if (looksLikeFestivalPass && Array.isArray(rawValidDays)) {
    if (rawValidDays.length === 3) return '3day';
    if (rawValidDays.length === 2) return '2day';
    if (rawValidDays.length === 1) return '1day';
  }
  return '';
};

const PerformerGrid = ({ performers, ticketPath, compact }) => (
  <div className={`music-ent-grid ${compact ? 'music-ent-grid-2' : ''}`}>
    {performers.map((performer, index) => (
      <Link
        key={`${performer.name}-${index}`}
        to={ticketPath}
        className="music-ent-card-link"
        title={performer.name}
      >
        <img
          loading="lazy"
          src={performer.image}
          alt={performer.name}
          className="music-ent-card-image"
        />
      </Link>
    ))}
  </div>
);

const StageSection = ({ name, ticketPath, buttonText, performers, onBuyTicketsClick }) => (
  <div className="music-ent-stage-block">
    <h3 className="music-ent-stage-title">
      {name}
      <span className="arabic-text on-white">{getArabicTranslation(name)}</span>
    </h3>
    <p className="music-ent-stage-note">
      Festival ticket required. Purchase festival tickets{' '}
      <button type="button" className="music-ent-inline-link" title={name} onClick={onBuyTicketsClick}>
        here
      </button>{' '}
      (<span className="music-ent-price-amount">starting at $20</span>)
    </p>
    <PerformerGrid performers={performers} ticketPath={ticketPath} compact={performers.length === 2} />
    <div className="music-ent-stage-btn-wrap">
      <button type="button" className="music-ent-stage-btn" onClick={onBuyTicketsClick}>
        {buttonText}
      </button>
    </div>
  </div>
);

const FestivalTickets = () => {
  const { content } = useCms();
  const cms = content?.ticketsPage || {};
  const daySections = buildLineupDaySections(getArtistLineup(content));
  const [error, setError] = useState('');
  const [salesMessage, setSalesMessage] = useState('');
  const [hasVendorInCart, setHasVendorInCart] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignDefaults, setAssignDefaults] = useState({ passSlug: '', quantity: 1 });
  const isStrictAdmin = useMemo(() => isCurrentUserStrictAdmin(), []);
  const isAdminSoldOutBypass = isStrictAdmin;
  // Prefill from the Convert-to-VIP flow: /festival-tickets?assignEmail=&assignFirst=&assignLast=
  const [searchParams] = useSearchParams();
  const assignPrefill = useMemo(() => ({
    email: searchParams.get('assignEmail') || '',
    first: searchParams.get('assignFirst') || '',
    last: searchParams.get('assignLast') || '',
  }), [searchParams]);
  const isPrefillAssignMode = useMemo(
    () => !!(assignPrefill.email || assignPrefill.first || assignPrefill.last),
    [assignPrefill.email, assignPrefill.first, assignPrefill.last]
  );
  const isAdminAssignMode = isStrictAdmin || isPrefillAssignMode;
  
  // Initialize quantities to 0 - no pre-selection, no localStorage persistence
  const [quantities, setQuantities] = useState({});
  const [realTickets, setRealTickets] = useState([]);
  const quantitiesInitializedRef = useRef(false);
  const ticketOptionsRef = useRef(null);

  // Fallback ticket structure for immediate card rendering (prices load separately)
  const fallbackTickets = [
    {
      id: '1day-fallback',
      name: '1-Day Pass',
      slug: '1day',
      description: 'Come enjoy the festival for a single day',
      price: null, // null = loading state
      sale_price: null,
      max_quantity: 999,
      sold_count: 999,
      remaining: 0,
      soldOut: !isAdminSoldOutBypass
    },
    {
      id: '2day-fallback',
      name: '2-Day Pass',
      slug: '2day',
      description: 'Come enjoy the festival for two days',
      price: null,
      sale_price: null,
      max_quantity: 999,
      sold_count: 999,
      remaining: 0,
      soldOut: !isAdminSoldOutBypass
    },
    {
      id: '3day-fallback',
      name: '3-Day Pass',
      slug: '3day',
      description: 'Come enjoy the festival for three days',
      price: null,
      sale_price: null,
      max_quantity: 999,
      sold_count: 999,
      remaining: 0,
      soldOut: !isAdminSoldOutBypass
    }
  ];

  // Always show all 3 canonical festival pass cards. For normal shoppers, they
  // remain sold out; strict admins get a bypass so they can run checkout tests.
  // The backend only
  // returns whichever pass types are still active (often just the 1-Day),
  // so instead of replacing the cards with that subset we keep the 3
  // canonical types and merge any real ticket data (matched by day number).
  const passDayDigit = (t) =>
    (t?.name?.match(/(\d)/)?.[1]) || (t?.slug?.match(/(\d)/)?.[1]) || '';
  const displayTickets = fallbackTickets.map((fb) => {
    const real = realTickets.find((rt) => passDayDigit(rt) === passDayDigit(fb));
    return real ? { ...real, soldOut: !isAdminSoldOutBypass } : fb;
  });
  // Every festival pass is sold out, so the Add-to-Cart / summary bar serves
  // no purpose for normal shoppers (it can still surface on mobile when a
  // vendor booth sits in the cart). Hide it entirely unless an admin is in
  // assign mode.
  const allSoldOut = !isAdminSoldOutBypass && displayTickets.length > 0 && displayTickets.every((t) => t.soldOut);

  // Fetch tickets with real-time price sync
  const fetchTickets = async () => {
    try {
      setError('');
      const response = isAdminSoldOutBypass
        ? await api.getAdminTicketTypes()
        : await api.getTicketTypes();
      if (response?.success && response.data?.length > 0) {
        // Keep only canonical 1/2/3-day festival passes. Admin endpoint returns
        // many extra rows (legacy/comp/etc), so normalize to one best row per
        // pass key to avoid card mismatches and "Loading..." placeholders.
        const withPassKey = response.data
          .map((ticket) => ({
            ...ticket,
            _passKey: detectFestivalPassKey(ticket?.slug, ticket?.name, ticket?.valid_days),
          }))
          .filter((ticket) => ticket._passKey);

        const canonicalByKey = {};
        withPassKey.forEach((ticket) => {
          const key = ticket._passKey;
          const slug = String(ticket.slug || '').toLowerCase();
          const score = (slug === key ? 2 : 0) + (ticket.is_active ? 1 : 0);

          const existing = canonicalByKey[key];
          if (!existing) {
            canonicalByKey[key] = ticket;
            return;
          }

          const existingSlug = String(existing.slug || '').toLowerCase();
          const existingScore = (existingSlug === key ? 2 : 0) + (existing.is_active ? 1 : 0);
          if (score > existingScore) {
            canonicalByKey[key] = ticket;
          }
        });

        const customerTickets = ['1day', '2day', '3day']
          .map((key) => canonicalByKey[key])
          .filter(Boolean);
        
        // Map backend tickets to our format
        const mappedTickets = customerTickets.map(ticket => ({
          id: ticket.id, // This is the UUID
          name: ticket.name,
          slug: ticket.slug,
          valid_days: Array.isArray(ticket.valid_days) ? ticket.valid_days : [],
          description: ticket.name.includes('3') ? 'Come enjoy the festival for three days' : ticket.name.includes('2') ? 'Come enjoy the festival for two days' : 'Come enjoy the festival for a single day',
          price: ticket.price_cents / 100, // Convert cents to dollars
          sale_price: ticket.sale_price_cents ? ticket.sale_price_cents / 100 : null,
          savings: ticket.sale_price_cents && (ticket.price_cents - ticket.sale_price_cents) > 0
            ? `Save $${((ticket.price_cents - ticket.sale_price_cents) / 100).toFixed(0)} on entry`
            : '',
          sale_message: ticket.sale_message || '',
          show_sale_message: ticket.show_sale_message || false,
          max_quantity: ticket.capacity || 0,
          sold_count: Number(ticket.sold_count || 0),
          remaining: Math.max(0, (ticket.capacity || 0) - Number(ticket.sold_count || 0)),
          soldOut: !isAdminSoldOutBypass
        }));
        // Sort: 1-Day first, then 2-Day, then 3-Day
        const dayOrder = { '1': 0, '2': 1, '3': 2 };
        mappedTickets.sort((a, b) => {
          const aDay = a.name.match(/(\d)/)?.[1] || '9';
          const bDay = b.name.match(/(\d)/)?.[1] || '9';
          return (dayOrder[aDay] ?? 9) - (dayOrder[bDay] ?? 9);
        });
        setRealTickets(mappedTickets);

        // ── Meta Pixel: ViewContent ──
        trackViewContent({
          contentName: 'Festival Tickets',
          contentCategory: 'Tickets',
          contentIds: mappedTickets.map(t => t.id),
          value: mappedTickets.reduce((min, t) => Math.min(min, t.sale_price || t.price), Infinity),
          currency: 'USD',
        });
        
        // Initialize quantities — pre-populate from existing cart so returning
        // users see what they already have and don't accidentally lose items
        if (!quantitiesInitializedRef.current) {
          const initialQuantities = {};
          mappedTickets.forEach((ticket) => {
            initialQuantities[ticket.id] = 0;
          });
          // Restore quantities from cart (if any festival tickets already added)
          try {
            const stored = localStorage.getItem('cart');
            if (stored) {
              const parsed = JSON.parse(stored);
              (parsed.items || []).forEach(item => {
                if (item.type === 'festival') {
                  const ticketId = item.ticket_type_id || item.id;
                  if (ticketId in initialQuantities) {
                    initialQuantities[ticketId] = item.quantity || 1;
                  }
                }
              });
            }
          } catch (e) { /* ignore parse errors */ }
          setQuantities(initialQuantities);
          quantitiesInitializedRef.current = true;
        }
      } else {
        setSalesMessage(response?.message || '');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Unable to load ticket pricing right now. Please refresh and try again.');
    }
  };

  useEffect(() => {
    fetchTickets();
    
    // Poll every 60 seconds for real-time price updates (but don't reset quantities)
    const pollInterval = setInterval(fetchTickets, 60000);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if cart has vendor booth items (mutual exclusivity)
  useEffect(() => {
    const checkVendorInCart = () => {
      try {
        const stored = localStorage.getItem('cart');
        if (stored) {
          const parsed = JSON.parse(stored);
          const items = parsed.items || [];
          const hasVendor = items.some(item => item.type === 'vendor-booth' || item.type === 'vendor' || item.type === 'vendor-attendee');
          setHasVendorInCart(hasVendor);
        } else {
          setHasVendorInCart(false);
        }
      } catch (e) {
        setHasVendorInCart(false);
      }
    };
    checkVendorInCart();
    window.addEventListener('cartUpdated', checkVendorInCart);
    window.addEventListener('storage', checkVendorInCart);
    return () => {
      window.removeEventListener('cartUpdated', checkVendorInCart);
      window.removeEventListener('storage', checkVendorInCart);
    };
  }, []);

  // Card click no longer resets quantities - users can set multiple ticket types

  // Simple increment/decrement handlers with inventory enforcement
  const increment = (id) => {
    const ticket = displayTickets.find(t => t.id === id);
    if (!isAdminSoldOutBypass && ticket && ticket.max_quantity > 0) {
      const remaining = ticket.max_quantity - (ticket.sold_count || 0);
      const currentQty = quantities[id] || 0;
      if (currentQty >= remaining) {
        toast.warning(`Only ${remaining} ${ticket.name} ticket${remaining !== 1 ? 's' : ''} remaining`);
        return;
      }
    }
    if (getTotalTickets() >= MAX_FESTIVAL_TICKETS_PER_ORDER) {
      toast.warning(`Maximum ${MAX_FESTIVAL_TICKETS_PER_ORDER} festival tickets allowed per order`);
      return;
    }
    setQuantities(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  };

  const decrement = (id) => {
    setQuantities(prev => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));
  };

  const getTotalPrice = () => {
    return displayTickets.reduce((total, ticket) => {
      const effectivePrice = ticket.sale_price || ticket.price;
      const quantity = quantities[ticket.id] || 0;
      return total + (effectivePrice * quantity);
    }, 0);
  };

  const getTotalTickets = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };

  const passOptions = useMemo(() => {
    const byKey = {};
    (realTickets || []).forEach((ticket) => {
      const key = detectFestivalPassKey(ticket?.slug, ticket?.name, ticket?.valid_days);
      if (!key || byKey[key]) return;
      byKey[key] = {
        key,
        slug: ticket.slug,
        name: key === '1day' ? '1-Day Pass' : key === '2day' ? '2-Day Pass' : '3-Day Pass',
      };
    });
    const ordered = ['1day', '2day', '3day'].map((key) => byKey[key]).filter(Boolean);
    if (ordered.length > 0) return ordered;
    return [
      { key: '1day', slug: '1day', name: '1-Day Pass' },
      { key: '2day', slug: '2day', name: '2-Day Pass' },
      { key: '3day', slug: '3day', name: '3-Day Pass' },
    ];
  }, [realTickets]);

  const openAdminAssignFestivalModal = () => {
    if (!isAdminAssignMode) return;
    if (realTickets.length === 0) {
      toast.error('Unable to load tickets. Please refresh the page.');
      return;
    }

    const selectedTickets = displayTickets.filter((t) => (quantities[t.id] || 0) > 0);
    if (!selectedTickets.length) {
      toast.error('Select at least one festival pass first.');
      return;
    }
    if (selectedTickets.length !== 1) {
      toast.error('For admin assignment, select exactly one festival pass type.');
      return;
    }

    const selected = selectedTickets[0];
    const selectedPassKey = detectFestivalPassKey(selected?.slug, selected?.name, selected?.valid_days);
    const selectedOption = passOptions.find((opt) => opt.slug === selected.slug)
      || passOptions.find((opt) => opt.key === selectedPassKey);
    if (!selectedOption) {
      toast.error('Selected ticket is not a 1-day, 2-day, or 3-day festival pass.');
      return;
    }

    setAssignDefaults({
      passSlug: selectedOption.slug || passOptions[0]?.slug || '1day',
      quantity: Math.max(1, parseInt(quantities[selected.id], 10) || 1),
    });
    setAssignError('');
    setShowAssignModal(true);
  };

  const submitAdminFestivalAssign = async (payload) => {
    setAssignLoading(true);
    setAssignError('');
    try {
      const res = await api.assignAdminUnsoldSeatOrPass(payload);
      if (res?.success) {
        toast.success(res.message || 'Festival pass assigned successfully.');
        setShowAssignModal(false);
        setQuantities((prev) => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}));
        await fetchTickets();
      } else {
        setAssignError(res?.error?.message || 'Failed to assign festival pass');
      }
    } catch (e) {
      setAssignError(e.message || 'Failed to assign festival pass');
    } finally {
      setAssignLoading(false);
    }
  };

  const scrollToTicketOptions = () => {
    if (!ticketOptionsRef.current) return;
    const y = ticketOptionsRef.current.getBoundingClientRect().top + window.pageYOffset - 110;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  return (
    <div className="page-wrapper">
      <AnnouncementBar />
      
      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="page-heading">OC MENA Festival Tickets</h1>
          
          <div className="page-intro">
            <p>Discover the excitement of the OC MENA Festival, a vibrant summer celebration in Orange County inspired by the rich cultures of the Middle East and North Africa. Enjoy three unforgettable days of music, food, carnival rides, cultural showcases, shopping, and family-friendly entertainment.</p>
            <p>Choose from single-day or full-weekend passes to experience everything the festival has to offer.</p>
          </div>

          <div className="tickets-header-section" ref={ticketOptionsRef}>
            <div className="option-1-badge">Option 1</div>
            <h2 className="card-title">Festival tickets<span className="arabic-text on-white">{getArabicTranslation('Festival Tickets')}</span></h2>
          </div>
          
          <div className={`tickets-container ${displayTickets.length === 1 ? 'single-ticket' : ''}`}>
            {displayTickets.map(ticket => (
              <div key={ticket.id} className={`ticket-card ${quantities[ticket.id] > 0 ? 'selected' : ''}`}>
                {quantities[ticket.id] > 0 && (
                  <div className="ticket-checkmark">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#cccccc" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                )}
                <div className="ticket-header">
                  <div className="ticket-pass-label">
                    {ticket.name}
                  </div>
                  <div className="ticket-pass-arabic">
                    <span className="arabic-text on-white">{getArabicTranslation(ticket.name)}</span>
                  </div>
                </div>
                
                <div className="ticket-product-image">
                  <img loading="lazy" 
                    src={ticket.name?.toLowerCase().includes('3') ? getCmsImagePath('product3DayTickets', '/product-3-day-tickets.webp') : ticket.name?.toLowerCase().includes('2') ? getCmsImagePath('product2DayTickets', '/product-2-day-tickets.webp') : getCmsImagePath('product1DayTickets', '/product-1-day-tickets.webp')} 
                    alt={ticket.name} 
                  />
                </div>

                {ticket.soldOut ? (
                  <div className="ticket-quantity-section" onClick={(e) => e.stopPropagation()}>
                    <div className="ticket-quantity">
                      <div className="quantity-label" style={{ color: '#dc2626', fontWeight: 700, fontSize: '1.1rem', textAlign: 'center', marginTop: '0.5rem' }}>
                        SOLD OUT
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ticket-quantity-section" onClick={(e) => e.stopPropagation()}>
                    <div className="ticket-quantity">
                      <div className="quantity-label">How many tickets?</div>
                      <div className="quantity-controls">
                        <button 
                          type="button"
                          className="quantity-btn"
                          onClick={() => decrement(ticket.id)}
                          disabled={quantities[ticket.id] === 0}
                        >
                          −
                        </button>
                        <div className="quantity-value">{quantities[ticket.id] || 0}</div>
                        <button 
                          type="button"
                          className="quantity-btn"
                          onClick={() => increment(ticket.id)}
                          disabled={!isAdminSoldOutBypass && (ticket.name?.includes('3-Day') || ticket.name?.includes('2-Day'))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="ticket-pricing">
                  {ticket.soldOut ? (
                    <span className="ticket-price" style={{ opacity: 0 }} aria-hidden="true">$0</span>
                  ) : ticket.price === null ? (
                    <span className="ticket-price" style={{ opacity: 0.5 }}>Loading...</span>
                  ) : ticket.sale_price ? (
                    <div className="pricing-with-sale">
                      <span className="ticket-price-regular">
                        ${(getTotalTickets() > 0 && quantities[ticket.id] > 0 ? (ticket.price * quantities[ticket.id]) : ticket.price).toLocaleString()}
                      </span>
                      <span className="ticket-price">${(getTotalTickets() > 0 && quantities[ticket.id] > 0 ? (ticket.sale_price * quantities[ticket.id]) : ticket.sale_price).toLocaleString()}</span>
                    </div>
                  ) : (
                    <span className="ticket-price">${(getTotalTickets() > 0 && quantities[ticket.id] > 0 ? (ticket.price * quantities[ticket.id]) : ticket.price).toLocaleString()}</span>
                  )}
                </div>
               
              </div>
            ))}
          </div>

          {(isAdminAssignMode || !allSoldOut) && (getTotalTickets() > 0 || hasVendorInCart) && (
            <div className="ticket-summary">
              {getTotalTickets() > 0 && (
                <div className="summary-row total">
                  <span>Subtotal:</span>
                  <span>${getTotalPrice().toLocaleString()}</span>
                </div>
              )}
              <button 
                type="button"
                className="add-to-cart-btn"
                disabled={isAdminAssignMode ? (realTickets.length === 0 || getTotalTickets() === 0 || assignLoading) : (realTickets.length === 0 || hasVendorInCart || getTotalTickets() === 0)}
                style={{
                  opacity: (isAdminAssignMode ? (getTotalTickets() === 0 || assignLoading) : (hasVendorInCart || getTotalTickets() === 0)) ? 0.7 : 1,
                  cursor: (isAdminAssignMode ? (getTotalTickets() === 0 || assignLoading) : (hasVendorInCart || getTotalTickets() === 0)) ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (isAdminAssignMode) {
                    openAdminAssignFestivalModal();
                    return;
                  }

                  // Validate that we have real tickets from backend (with UUIDs)
                  if (realTickets.length === 0) {
                    toast.error('Unable to load tickets. Please refresh the page.');
                    return;
                  }
                  
                  // Validate all selected tickets have valid UUIDs (not fallback data)
                  const selectedTickets = displayTickets.filter(t => quantities[t.id] > 0);
                  const hasInvalidTickets = selectedTickets.some(t => !t.id || t.id.length < 20 || !t.id.includes('-'));
                  if (hasInvalidTickets) {
                    toast.error('Invalid ticket data. Please refresh the page and try again.');
                    return;
                  }
                  
                  const newItems = selectedTickets
                    .map(t => ({ 
                      id: t.id,
                      ticket_type_id: t.id, // UUID from backend
                      name: t.name,
                      quantity: quantities[t.id],
                      price: t.sale_price || t.price, // Use sale price if available
                      type: 'festival' // Mark as festival ticket
                    }));
                  
                  // Get existing cart and merge (preserve amphitheater and other items)
                  let existingCart = [];
                  try {
                    const stored = localStorage.getItem('cart');
                    if (stored) {
                      const parsed = JSON.parse(stored);
                      existingCart = parsed.items || [];
                    }
                  } catch (e) {
                    console.error('Error parsing cart:', e);
                  }
                  
                  // Remove old festival tickets and keep other items (amphitheater only — vendor is separate transaction)
                  const nonFestivalItems = existingCart.filter(item => 
                    item.type === 'amphitheater' || 
                    item.sectionId || // Amphitheater items have sectionId
                    item.holdIds // Amphitheater items have holdIds
                  );
                  
                  // Merge: keep non-festival items + add new festival items
                  const mergedItems = [...nonFestivalItems, ...newItems];
                  
                  // Calculate new total
                  const newTotal = mergedItems.reduce((sum, item) => {
                    const qty = item.quantity || 1;
                    return sum + (item.price * qty);
                  }, 0);
                  
                  // Save merged cart to localStorage
                  localStorage.setItem('cart', JSON.stringify({
                    items: mergedItems,
                    total: newTotal
                  }));
                  
                  // ── Meta Pixel: AddToCart ──
                  const addedTotal = newItems.reduce((s, i) => s + (i.price * i.quantity), 0);
                  trackAddToCart({
                    contentName: newItems.map(i => i.name).join(', '),
                    contentIds: newItems.map(i => i.id),
                    value: addedTotal,
                    currency: 'USD',
                    numItems: newItems.reduce((s, i) => s + i.quantity, 0),
                  });

                  // Dispatch custom event to update cart and open sidebar
                  window.dispatchEvent(new CustomEvent('cartUpdated', { 
                    detail: { items: mergedItems, total: newTotal } 
                  }));
                  
                  // Dispatch event to open cart sidebar
                  window.dispatchEvent(new CustomEvent('openCart'));
                }}
              >
                {isAdminAssignMode ? (assignLoading ? 'Assigning…' : 'Add Assignment') : 'Add Tickets to Cart'}
              </button>

              {hasVendorInCart && !isAdminAssignMode && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <span className="funnel-option-badge" style={{ fontSize: '13px', padding: '10px 18px', lineHeight: '1.6', whiteSpace: 'normal', maxWidth: '600px', display: 'inline-block', background: '#FDF2C6', color: '#D87706' }}>
                    You have a vendor product added to your cart. You can purchase tickets before or after your vendor booth transaction, but not during the same time. Any tickets added to your cart will not be processed until you finish checkout with the vendor booth.
                  </span>
                </div>
              )}
            </div>
          )}

          {salesMessage && (
            <div className="sales-message" style={{textAlign: 'center', padding: '1rem', backgroundColor: 'rgba(255,193,7,0.1)', borderRadius: '8px', marginBottom: '1rem'}}>
              <p style={{color: '#856404', margin: 0}}>{salesMessage}</p>
            </div>
          )}

          {error && (
            <div className="error-message" style={{textAlign: 'center', padding: '1rem', backgroundColor: 'rgba(220,53,69,0.1)', borderRadius: '8px', marginBottom: '1rem'}}>
              <p style={{color: '#721c24', margin: 0}}>{error}</p>
            </div>
          )}

          <div className="parking-notice" style={{ marginBottom: '2rem' }}>
            <p>*Parking is ${cms.parkingPrice || '15'}, paid directly to the OC Fair & Events Center on site. Carpooling is encouraged.</p>
            <p>*Rides at the festival range from $2-10/person. *Festival tickets do not include access to the amphitheater.</p>
          </div>

          <section className="music-ent-container music-ent-compact">
            {daySections.map((day) => (
              <article key={day.key} className="music-ent-day-section">
                <header className="music-ent-day-header">
                  <h2>
                    {day.dayLabel}
                    <span className={`arabic-text on-dark arabic-inline ${day.key === 'sunday' ? 'sunday-arabic' : ''}`}>{getArabicTranslation(day.dayLabel)}</span>
                  </h2>
                  <p>{day.dateLabel}</p>
                </header>

                {day.stages.map((stage) => (
                  <StageSection
                    key={`${day.key}-${stage.name}`}
                    name={stage.name}
                    ticketPath={stage.ticketPath}
                    buttonText={stage.buttonText}
                    performers={stage.performers}
                    onBuyTicketsClick={scrollToTicketOptions}
                  />
                ))}
              </article>
            ))}
          </section>

          <div className="music-ent-support-images" style={{ marginTop: '1.5rem' }}>
            <div className="music-ent-support-image-frame">
              <img
                loading="lazy"
                src="/ocmena-official-flyer(1080x1350)-c.png"
                alt="OC MENA Festival Official Flyer"
                className="music-ent-support-image"
              />
            </div>
            <div className="music-ent-support-image-frame">
              <img loading="lazy" src="/how_tickets_work.jpeg" alt="How tickets work" className="music-ent-support-image" />
            </div>
            <div className="music-ent-support-image-frame">
              <img
                loading="lazy"
                src="/map.png"
                alt="OC MENA Festival Event Map"
                className="music-ent-support-image"
              />
            </div>
          </div>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath("lanterns", "/lanterns.webp")} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
      <AdminUnsoldAssignModal
        open={showAssignModal}
        onClose={() => { if (!assignLoading) { setShowAssignModal(false); setAssignError(''); } }}
        onSubmit={submitAdminFestivalAssign}
        loading={assignLoading}
        error={assignError}
        assignmentType="festival_pass"
        passOptions={passOptions}
        initialPassSlug={assignDefaults.passSlug}
        initialQuantity={assignDefaults.quantity}
        defaultEmail={assignPrefill.email}
        defaultFirstName={assignPrefill.first}
        defaultLastName={assignPrefill.last}
      />
    </div>
  );
};

export default FestivalTickets;
